import { AbsolutePopoverHostName } from "@/src/components/mobile/AbsolutePopover";
import { Portal } from "@gorhom/portal";
import {
  AHIcon,
  AHText,
  Box,
  Circle,
  PublicOrPrivateImage,
  useBoxStyle,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { useCallback, useEffect, useRef } from "react";
import { FullWindowOverlay } from "react-native-screens";
import { Platform, View } from "react-native";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { EmployeeToastComponent_employee$key } from "./__generated__/EmployeeToastComponent_employee.graphql";

import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { EmployeeToastQuery } from "./__generated__/EmployeeToastQuery.graphql";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { BlurView } from "expo-blur";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Animated, {
  runOnJS,
  SlideInUp,
  withTiming,
  useAnimatedStyle,
  withSpring,
  useSharedValue,
} from "react-native-reanimated";
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from "react-native-gesture-handler";
import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

import { getInitials } from "widgets";
import { useCurrentEmployeeSelectControllerState } from "./useCurrentEmployeeSelectControllerState";

function ToastContainer(props: { children: React.ReactNode }) {
  if (Platform.OS === "ios") {
    return <FullWindowOverlay>{props.children}</FullWindowOverlay>;
  }
  return <Portal hostName={AbsolutePopoverHostName}>{props.children}</Portal>;
}

function Toast(props: {
  onPress: () => void;
  close: () => void;
  children: React.ReactNode;
}) {
  const scale = useSharedValue<number>(1);
  const offset = useSharedValue<number>(0);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const clearTimeoutId = useCallback(() => {
    if (timeoutId.current !== null) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  }, []);
  const setTimeoutGestureEnd = useCallback(() => {
    clearTimeoutId();
    timeoutId.current = setTimeout(() => {
      offset.value = withSpring(-200, {}, () => {
        runOnJS(props.close)();
      });
    }, 3000);
    return () => {
      clearTimeoutId();
    };
  }, [clearTimeoutId, offset, props.close]);

  const press = Gesture.Tap()
    .onStart(() => {
      runOnJS(clearTimeoutId)();
      scale.value = withTiming(0.98);
    })
    .onEnd(() => {
      scale.value = withTiming(1);
      runOnJS(props.onPress)();
    });

  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const pan = Gesture.Pan()
    .onStart(() => {
      runOnJS(clearTimeoutId)();
    })
    .onChange((event) => {
      offset.value = event.translationY;
      if (event.translationY > 15) {
        offset.value =
          15 + (event.translationY - 15) / Math.log(event.translationY);
      }
    })
    .onFinalize((event) => {
      runOnJS(clearTimeoutId)();
      if (offset.value < -10) {
        offset.value = withSpring(
          -200,
          {
            velocity: event.velocityY,
          },
          () => {
            runOnJS(props.close)();
          }
        );
      } else {
        offset.value = withSpring(0, {
          velocity: event.velocityY,
          clamp: { min: insets.top + layout.space.xsmall, max: 200 },
        });
        runOnJS(setTimeoutGestureEnd)();
      }
    });

  const combined = Gesture.Race(press, pan);

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [{ translateY: offset.value }, { scale: scale.value }],
  }));
  const boxStyle = useBoxStyle({
    cornerRadius: "large",
    shadow: "strong",
    color: "transparent",
  });
  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  useEffect(() => {
    timeoutId.current = setTimeout(() => {
      offset.value = withSpring(-200, {}, () => {
        runOnJS(props.close)();
      });
    }, 5000);
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [offset, props.close]);

  return (
    <Box
      style={{
        position: "absolute",
        alignSelf: "center",
        top: Math.max(insets.top + layout.space.xsmall, layout.space.medium),
        left: isCompact ? 0 : undefined,
        right: isCompact ? 0 : undefined,
        maxWidth: "100%",
        minWidth: 400,
      }}
      spaceHorizontal="medium"
    >
      <Animated.View entering={SlideInUp.duration(500)}>
        <GestureHandlerRootView>
          <GestureDetector gesture={combined}>
            <Animated.View style={[boxStyle, animatedStyles]}>
              <Blur>
                <Box
                  style={{ flexDirection: "row", alignItems: "center" }}
                  space="medium"
                >
                  {props.children}
                </Box>
              </Blur>
            </Animated.View>
          </GestureDetector>
        </GestureHandlerRootView>
      </Animated.View>
    </Box>
  );
}

function InnerBlur(props: { children: React.ReactNode }) {
  const layout = useLayoutContext();
  return (
    <BlurView style={{ borderRadius: layout.radius.large, overflow: "hidden" }}>
      {props.children}
    </BlurView>
  );
}

function InnerAndroidBlur(props: { children: React.ReactNode }) {
  const layout = useLayoutContext();
  const theme = useTheme();
  return (
    <View
      style={{
        backgroundColor: theme["surface-primary"],
        borderRadius: layout.radius.large,
        overflow: "hidden",
      }}
    >
      {props.children}
    </View>
  );
}

const Blur = Platform.select({
  android: InnerAndroidBlur,
  default: InnerBlur,
});

function EmployeeToastComponent(props: {
  employee: EmployeeToastComponent_employee$key | null;
}) {
  const { openModal, dismiss, } =
    useCurrentEmployeeSelectControllerState();
  const employee = useFragment(
    graphql`
      fragment EmployeeToastComponent_employee on Employee {
        name
        image {
          ...ImagePublicOrPrivate_image
        }
      }
    `,
    props.employee
  );

  const intl_tokens = useIntlTokens();

  return (
    <ToastContainer>
      <Toast onPress={openModal} close={dismiss}>
        <Circle
          size="large"
          color="surface-primary"
          style={{
            overflow: "hidden",
          }}
        >
          {employee ? (
            employee.image ? (
              <PublicOrPrivateImage
                image={employee.image}
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <AHText type="title-small">
                {getInitials(employee?.name ?? "")}
              </AHText>
            )
          ) : (
            <AHIcon type="guest-actor" />
          )}
        </Circle>
        <Box
          style={{
            flexDirection: "column",
            justifyContent: "center",
            flex: 1,
          }}
          spaceHorizontal="medium"
        >
          <AHText type="detail" color="text-secondary">
            <FormattedMessage
              defaultMessage="Active Profile (Group Account)"
              description="EmployeeSwitch: Active Profile"
            />
          </AHText>
          <AHText type="title" numberOfLines={1} ellipsizeMode="tail">
            {employee?.name ?? intl_tokens.Guest}
          </AHText>
        </Box>
        <AHIcon type="shuffle" size="small" color="text-secondary" />
      </Toast>
    </ToastContainer>
  );
}

function InnerEmployeeToast(props: { fetchKey: string }) {
  const { currentEmployeeId } = usePersistedEmployeeId();
  const data = useLazyLoadQuery<EmployeeToastQuery>(
    graphql`
      query EmployeeToastQuery($employeeId: ID) {
        currentStore {
          employee(id: $employeeId) {
            ...EmployeeToastComponent_employee
          }
        }
      }
    `,
    {
      employeeId: currentEmployeeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  return (
    <EmployeeToastComponent employee={data.currentStore?.employee ?? null} />
  );
}

export function EmployeeToast() {
  return (
    <LazyQueryNetworkBoundrary fallback={null} errorFallback={() => null}>
      {(fetchKey) => <InnerEmployeeToast fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
